/**
 * usePerformanceMenuTriggers - Menu trigger render functions for PerformanceAnalytics
 *
 * Extracts menu trigger rendering logic from the Container to reduce file size.
 * Compare/timeframe trigger text is truncated with min-w-0 + shrink (RN
 * flexShrink defaults to 0, unlike web's 1), max-w-48, numberOfLines={1}, and
 * isTruncated. Combined with justify-between on the compare pill, this keeps the
 * chevron inside the box on the narrow mobile half-column instead of overflowing
 * past the right border, on both native and web platforms.
 * @remarks This is a custom hook that returns render functions for dropdown triggers
 */

import { ChevronDown } from "lucide-react-native";
import React, { useCallback } from "react";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import {
  FixedPositionDropdownShellTrigger,
  type FixedPositionDropdownShellTriggerRef,
  type DropdownMeasurableNode,
} from "@/components/atoms/FixedPositionDropdownShell";
import type { MetricOption } from "@/features/player-detail/utils/performanceAnalyticsUtils";

/**
 * Get classes for dropdown button styling
 * @returns Tailwind class string for dropdown button
 */
const getDropdownButtonClasses = (): string =>
  "flex-row items-center gap-2 rounded-md border border-surface-muted bg-surface-raised px-3 py-2";

/**
 * Get classes for dot plot axis trigger styling
 * @param isDark - Whether dark mode is active
 * @returns Tailwind class string for axis trigger button
 */
const getDotPlotTriggerClasses = (isDark: boolean): string =>
  `flex-row items-center gap-1 rounded-md bg-surface-raised px-3 py-1 ${
    isDark ? "" : "border border-outline-strong"
  }`;

/**
 * Props for usePerformanceMenuTriggers hook
 */
interface UsePerformanceMenuTriggersProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Selected compare item name */
  readonly selectedCompareItemName: string;
  /** Selected timeframe label */
  readonly selectedTimeframeLabel: string;
  /** X-axis metric */
  readonly xAxisMetric: MetricOption;
  /** Y-axis metric */
  readonly yAxisMetric: MetricOption;
}

/**
 * Props the compare/timeframe triggers receive from the FixedPositionDropdownShell
 * host (SE-5551): the open state, the toggle handler, and the measurement ref.
 */
export interface PerformanceShellTriggerProps {
  /** Whether the dropdown panel is open (drives a11y expanded). */
  readonly isOpen: boolean;
  /** Toggle the panel open/closed. */
  readonly onPress: () => void;
  /** Measurement ref from `useDropdownPositioning`. */
  readonly triggerRef: React.RefObject<DropdownMeasurableNode | null>;
}

/**
 * Return type for usePerformanceMenuTriggers hook
 */
interface UsePerformanceMenuTriggersReturn {
  /** Render function for compare menu trigger (shell-hosted). */
  readonly renderCompareMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.ReactElement;
  /** Render function for timeframe menu trigger (shell-hosted). */
  readonly renderTimeframeMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.ReactElement;
  /** Render function for X-axis menu trigger (shell-hosted, SE-5568). */
  readonly renderXMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.ReactElement;
  /** Render function for Y-axis menu trigger (shell-hosted, SE-5568). */
  readonly renderYMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.ReactElement;
}

/**
 * Hook that provides menu trigger render functions for PerformanceAnalytics
 * @param props - Hook props
 * @param props.isDark - Whether dark mode is active
 * @param props.selectedCompareItemName - Selected compare item name
 * @param props.selectedTimeframeLabel - Selected timeframe label
 * @param props.xAxisMetric - X-axis metric
 * @param props.yAxisMetric - Y-axis metric
 * @returns Object containing menu trigger render functions
 */
export const usePerformanceMenuTriggers = ({
  isDark,
  selectedCompareItemName,
  selectedTimeframeLabel,
  xAxisMetric,
  yAxisMetric,
}: UsePerformanceMenuTriggersProps): UsePerformanceMenuTriggersReturn => {
  const renderCompareMenuTrigger = useCallback(
    ({ isOpen, onPress, triggerRef }: PerformanceShellTriggerProps) => (
      <FixedPositionDropdownShellTrigger
        ref={
          triggerRef as React.RefObject<FixedPositionDropdownShellTriggerRef>
        }
        isOpen={isOpen}
        onPress={onPress}
        accessibilityLabel={`Compare against ${selectedCompareItemName}, tap to change`}
        testID="compare-dropdown-button"
      >
        <Stack
          direction="horizontal"
          className={`${getDropdownButtonClasses()} justify-between`}
        >
          <Text
            variant="body-strong"
            className="min-w-0 max-w-48 shrink text-content-primary"
            isTruncated
            numberOfLines={1}
          >
            {selectedCompareItemName}
          </Text>
          <Icon as={ChevronDown} size="sm" className="text-content-secondary" />
        </Stack>
      </FixedPositionDropdownShellTrigger>
    ),
    [selectedCompareItemName]
  );

  const renderTimeframeMenuTrigger = useCallback(
    ({ isOpen, onPress, triggerRef }: PerformanceShellTriggerProps) => (
      <FixedPositionDropdownShellTrigger
        ref={
          triggerRef as React.RefObject<FixedPositionDropdownShellTriggerRef>
        }
        isOpen={isOpen}
        onPress={onPress}
        accessibilityLabel={`Timeframe ${selectedTimeframeLabel}, tap to change`}
        testID="timeframe-dropdown-button"
      >
        <Stack
          direction="horizontal"
          className={`${getDropdownButtonClasses()} justify-between`}
        >
          <Text
            variant="body-strong"
            className="min-w-0 max-w-48 shrink text-content-primary"
            isTruncated
            numberOfLines={1}
          >
            {selectedTimeframeLabel}
          </Text>
          <Icon as={ChevronDown} size="sm" className="text-content-secondary" />
        </Stack>
      </FixedPositionDropdownShellTrigger>
    ),
    [selectedTimeframeLabel]
  );

  const renderYMenuTrigger = useCallback(
    ({ isOpen, onPress, triggerRef }: PerformanceShellTriggerProps) => (
      <FixedPositionDropdownShellTrigger
        ref={
          triggerRef as React.RefObject<FixedPositionDropdownShellTriggerRef>
        }
        isOpen={isOpen}
        onPress={onPress}
        accessibilityLabel={`Y-axis metric ${yAxisMetric.label}, tap to change`}
      >
        <Stack
          direction="horizontal"
          className={getDotPlotTriggerClasses(isDark)}
        >
          <Text variant="body-strong" className="text-content-primary">
            {yAxisMetric.label}
          </Text>
          <Text variant="body-bold" className="text-content-primary">
            {yAxisMetric.value}
          </Text>
          <Icon as={ChevronDown} size="xs" className="text-content-muted" />
        </Stack>
      </FixedPositionDropdownShellTrigger>
    ),
    [isDark, yAxisMetric.label, yAxisMetric.value]
  );

  const renderXMenuTrigger = useCallback(
    ({ isOpen, onPress, triggerRef }: PerformanceShellTriggerProps) => (
      <FixedPositionDropdownShellTrigger
        ref={
          triggerRef as React.RefObject<FixedPositionDropdownShellTriggerRef>
        }
        isOpen={isOpen}
        onPress={onPress}
        accessibilityLabel={`X-axis metric ${xAxisMetric.label}, tap to change`}
      >
        <Stack
          direction="horizontal"
          className={getDotPlotTriggerClasses(isDark)}
        >
          <Text variant="body-strong" className="text-content-primary">
            {xAxisMetric.label}
          </Text>
          <Text variant="body-bold" className="text-content-primary">
            {xAxisMetric.value}
          </Text>
          <Icon as={ChevronDown} size="xs" className="text-content-muted" />
        </Stack>
      </FixedPositionDropdownShellTrigger>
    ),
    [isDark, xAxisMetric.label, xAxisMetric.value]
  );

  return {
    renderCompareMenuTrigger,
    renderTimeframeMenuTrigger,
    renderXMenuTrigger,
    renderYMenuTrigger,
  };
};
